/**
 * Global registry for the active 3D scene's interior light.
 * Allows the dev LightingPanel to control the light without prop drilling.
 *
 * The first mounted Room3DViewer with an interior light registers itself here.
 * When unmounted it unregisters. The panel polls this for the current light.
 */

import * as THREE from 'three';

export type ViewerType = 'room' | 'wall' | 'ceiling';

export interface LightingRegistryEntry {
  viewerType: ViewerType;
  light: THREE.PointLight;
  hemiLight: THREE.HemisphereLight | null;
  probe: THREE.LightProbe | null;
  envMap: THREE.Texture | null;
  /** Floor mesh for texture swapping (room only) */
  floorMesh: THREE.Mesh | null;
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  roomGroup: THREE.Group;
  renderScene: () => void;
  getRotation: () => { x: number; y: number };
  roomDimensions: { length: number; width: number; height: number };
}

/**
 * Get all MeshStandardMaterials from the roomGroup LIVE (not cached).
 * Excludes the floor mesh.
 */
export function getWallMaterials(entry: LightingRegistryEntry): THREE.MeshStandardMaterial[] {
  const mats: THREE.MeshStandardMaterial[] = [];
  entry.roomGroup.traverse(child => {
    if (child === entry.floorMesh) return;
    if (child instanceof THREE.Mesh && child.material instanceof THREE.MeshStandardMaterial) {
      mats.push(child.material);
    }
  });
  return mats;
}

let current: LightingRegistryEntry | null = null;
const listeners: Set<() => void> = new Set();

export function registerLight(entry: LightingRegistryEntry): void {
  current = entry;
  listeners.forEach(fn => fn());
}

export function unregisterLight(entry: LightingRegistryEntry): void {
  if (current === entry) {
    current = null;
    listeners.forEach(fn => fn());
  }
}

export function getRegisteredLight(): LightingRegistryEntry | null {
  return current;
}

export function onLightChange(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
